import * as React from "react";
import { FormControl, Grid, InputLabel, OutlinedInput } from "@mui/material";
import { Controller } from "react-hook-form";

import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { FilterOption, Option } from "@/components/customTable/types";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";
import { FilterFetchMultiSelect } from "@/components/fetch-multi-select";
import { FilterFetchSelect } from "@/components/fetch-select";

export function BasicInformation({
	control,
	errors,
	setValue,
	groupTitle,
	groupId,
	agentTitle,
	agentId,
	phoneNumbers,
	campaign,
	mainFields,
}: {
	control: any;
	errors: any;
	setValue: any;
	groupTitle: any;
	groupId: any;
	agentTitle: any;
	agentId: any;
	phoneNumbers: any;
	campaign: any;
	mainFields: any;
}) {
	return (
		<Grid container spacing={2}>
			<Grid
				size={{
					md: 12,
					xs: 12,
				}}
			>
				{" "}
				<Controller
					control={control}
					name="title"
					rules={{
						required: "Title is required",
						minLength: {
							value: 2,
							message: "Title must be at least 2 characters",
						},
					}}
					render={({ field, fieldState }) => (
						<FormControl error={Boolean(fieldState.error)} fullWidth required>
							<InputLabel>Title</InputLabel>
							<OutlinedInput {...field} placeholder="Enter title" />
							{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
						</FormControl>
					)}
				/>
			</Grid>

			<Grid
				size={{
					md: 12,
					xs: 12,
				}}
			>
				<Controller
					control={control}
					name="groupId"
					render={() => (
						<FormControl error={Boolean(errors.groupId)} fullWidth>
							<FilterFetchSelect
								label="Group"
								showInputLabel
								errorText={errors.groupId?.message as string}
								value={{ label: groupTitle, value: groupId } as FilterOption}
								placeholder="Select a group"
								onChange={(selected: FilterOption | string) => {
									const value = typeof selected === "string" ? selected : (selected.value as string) || "";
									const label = typeof selected === "string" ? selected : (selected.label as string) || "";

									setValue("groupId", value);
									setValue("groupTitle", label);
								}}
								getOptionsUrl="/crm/groups"
								fetchLabel="name"
								fetchValue="id"
							/>
							{errors.groupId ? <CustomFormHelperError text={(errors.groupId.message as string) || ""} /> : null}
						</FormControl>
					)}
				/>
			</Grid>

			<Grid
				size={{
					md: 12,
					xs: 12,
				}}
			>
				<Controller
					control={control}
					name="agentId"
					render={() => (
						<FormControl error={Boolean(errors.agentId)} fullWidth>
							<FilterFetchSelect
								label="Agent"
								showInputLabel
								errorText={errors.agentId?.message as string}
								value={{ label: agentTitle, value: agentId } as FilterOption}
								placeholder="Select an agent"
								onChange={(selected: FilterOption | string) => {
									const value = typeof selected === "string" ? selected : (selected.value as string) || "";
									const label = typeof selected === "string" ? selected : (selected.label as string) || "";

									setValue("agentId", value);
									setValue("agentTitle", label);
								}}
								getOptionsUrl="/agents"
								fetchLabel="name"
								fetchValue="id"
							/>
							{errors.agentId ? <CustomFormHelperError text={(errors.agentId.message as string) || ""} /> : null}
						</FormControl>
					)}
				/>
			</Grid>

			<Grid
				size={{
					md: 12,
					xs: 12,
				}}
			>
				<Controller
					control={control}
					name="phoneNumbers"
					render={() => (
						<FormControl error={Boolean(errors.phoneNumbers)} fullWidth>
							<FilterFetchMultiSelect
								label="Phone Numbers"
								showInputLabel
								errorText={errors.phoneNumbers?.message as string}
								value={phoneNumbers}
								placeholder="Select phone numbers"
								onChange={(selected: Option[]) => {
									setValue("phoneNumbers", selected);
								}}
								getOptionsUrl="/phone-numbers"
								fetchLabel="number"
								fetchValue="id"
								indicatorKey="virtual"
								indicatorTooltip="Virtual Number"
								disabledConditionKey={"campaign"}
								notDisabledConditionKey={"enabled"}
								notDisabledOptions={campaign?.phoneNumbers?.map((phone: any) => phone.id)}
							/>
							{errors.phoneNumbers ? (
								<CustomFormHelperError text={(errors.phoneNumbers.message as string) || ""} />
							) : null}
						</FormControl>
					)}
				/>
			</Grid>

			{mainFields?.map((field: any) => (
        <DynamicField
          control={control}
          type={field.type}
          name={`settings.${field.name}`}
          label={field.label}
          dynamicField={field}
          dependsOn={field.dependsOn}
          formName="settings"
          gridSize={{ xs: 12, md: 12 }}
          key={field.id}
        />
			))}

			<Grid
				size={{
					md: 12,
					xs: 12,
				}}
			>
				<DynamicField
					control={control}
					type={DynamicFieldType.BOOLEAN}
					name="enabled"
					label="Enabled"
					dynamicField={{
						name: "enabled",
						type: DynamicFieldType.BOOLEAN,
						label: "Enabled",
					}}
				/>
			</Grid>
		</Grid>
	);
}
